/* Public "Forgot password?" page. Enter email → server sends reset link. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your email");
    setBusy(true);
    try {
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      await fetch(`${backend}/api/auth/request-reset`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setSent(true);
    } catch (err) {
      toast.error("Could not send reset link — try again in a moment.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
            <KeyRound className="text-primary" size={18} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-primary">Zaroori Jini OMS</div>
            <div className="text-lg font-black">Reset your password</div>
          </div>
        </div>

        {sent ? (
          <div className="rounded-lg border border-success/40 bg-success/5 p-4 flex items-start gap-3" data-testid="forgot-sent">
            <CheckCircle2 className="text-success shrink-0 mt-0.5" size={18} />
            <div className="text-sm">
              <b>Check your inbox</b><br />
              <span className="text-muted-foreground">If <b>{email}</b> exists in our system, we&apos;ve sent a reset link valid for 1 hour. Don&apos;t forget to check spam.</span>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-sm text-muted-foreground">Enter the email tied to your account and we&apos;ll email you a secure link to set a new password.</p>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="you@company.com" data-testid="forgot-email"
                     className="mt-1" autoFocus required />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit">
              {busy ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}
        <div className="mt-6 pt-4 border-t border-border">
          <Link to="/login" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid="back-to-login">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
